// GtAnonCards: the review step for an anonymous share (C2).
// Each person gets a card written by AI from their full profile, then checked
// for words from their real profile and searched on the open web. Only cards
// that pass both can go on the link. Jimmy can edit any card (it is checked
// again), ask for a rewrite, or leave the person off the link.
import { useState, useEffect, useCallback, useRef } from 'react';

const STATUS = {
  writing: ['Writing', 'gtac-st--busy'],
  checking: ['Checking', 'gtac-st--busy'],
  clear: ['Clear to share', 'gtac-st--ok'],
  blocked: ['Worth a look', 'gtac-st--warn'],
  failed: ['Not checked', 'gtac-st--warn'],
};

function hostOf(u) { try { return new URL(u).hostname.replace(/^www\./, ''); } catch (e) { return u; } }

// The card as the client will see it, in miniature.
export function AnonCardPreview({ card, risky }) {
  const k = card || {};
  const marks = (risky || []).map((r) => String(r).toLowerCase()).filter(Boolean);
  const hl = (text) => {
    const t = String(text || '');
    const hit = marks.find((m) => t.toLowerCase().indexOf(m) !== -1);
    if (!hit) return t;
    const i = t.toLowerCase().indexOf(hit);
    return <>{t.slice(0, i)}<mark className="gtac-mark">{t.slice(i, i + hit.length)}</mark>{hl(t.slice(i + hit.length))}</>;
  };
  const facts = [k.seniority, k.years_experience ? 'about ' + k.years_experience + ' years' : null, k.company_type, k.location].filter(Boolean);
  return (
    <div className="gtac-prev">
      <div className="gtac-prev-h">{hl(k.headline) || <span className="gtac-dim">No headline yet</span>}</div>
      {facts.length > 0 && <div className="gtac-prev-facts">{facts.map((f, i) => <span key={i} className="gtac-fact">{hl(f)}</span>)}</div>}
      {(k.salary_band || k.notice) && (
        <div className="gtac-prev-terms">
          {k.salary_band && <span><em>Salary band</em>{k.salary_band}</span>}
          {k.notice && <span><em>Availability</em>{k.notice}</span>}
        </div>
      )}
      {k.highlights && k.highlights.length > 0 && <ul className="gtac-prev-hl">{k.highlights.map((h, i) => <li key={i}>{hl(h)}</li>)}</ul>}
      {k.career && k.career.length > 0 && (
        <div className="gtac-prev-career">
          {k.career.map((r, i) => (
            <div key={i} className="gtac-prev-role"><b>{hl(r.role)}</b><span>{hl(r.company_type)}</span><i>{r.years}</i></div>
          ))}
        </div>
      )}
      {(k.skills && k.skills.length > 0) && <div className="gtac-prev-skills">{k.skills.map((s, i) => <span key={i}>{hl(s)}</span>)}</div>}
      {k.education && <div className="gtac-prev-edu">{hl(k.education)}</div>}
    </div>
  );
}

function toForm(card) {
  const k = card || {};
  return {
    headline: k.headline || '', seniority: k.seniority || '', company_type: k.company_type || '',
    years_experience: k.years_experience ? String(k.years_experience) : '', location: k.location || '',
    highlights: (k.highlights || []).join('\n'),
    career: (k.career || []).map((r) => [r.role, r.company_type, r.years].map((x) => x || '').join(' | ')).join('\n'),
    skills: (k.skills || []).join(', '), education: k.education || '',
  };
}
function fromForm(f) {
  return {
    headline: f.headline, seniority: f.seniority, company_type: f.company_type,
    years_experience: f.years_experience ? Number(f.years_experience) : null, location: f.location,
    highlights: f.highlights.split('\n').map((x) => x.trim()).filter(Boolean),
    career: f.career.split('\n').map((l) => l.split('|').map((x) => x.trim())).filter((p) => p[0] || p[1])
      .map((p) => ({ role: p[0] || '', company_type: p[1] || '', years: p[2] || '' })),
    skills: f.skills.split(',').map((x) => x.trim()).filter(Boolean),
    education: f.education || null,
  };
}

function CardEditor({ card, onCancel, onSave, busy }) {
  const [f, setF] = useState(() => toForm(card));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="gtac-edit">
      <label className="gtac-f gtac-f--wide"><span>Headline</span><input value={f.headline} onChange={set('headline')} maxLength={120} /></label>
      <div className="gtac-frow">
        <label className="gtac-f"><span>Seniority</span><input value={f.seniority} onChange={set('seniority')} /></label>
        <label className="gtac-f"><span>Years</span><input value={f.years_experience} onChange={set('years_experience')} inputMode="numeric" /></label>
        <label className="gtac-f"><span>Location</span><input value={f.location} onChange={set('location')} /></label>
      </div>
      <label className="gtac-f gtac-f--wide"><span>Current company type</span><input value={f.company_type} onChange={set('company_type')} placeholder="e.g. Series C fintech" /></label>
      <label className="gtac-f gtac-f--wide"><span>What they have done <em>one per line</em></span><textarea rows={4} value={f.highlights} onChange={set('highlights')} /></label>
      <label className="gtac-f gtac-f--wide"><span>Career <em>role | company type | how long, one per line</em></span><textarea rows={3} value={f.career} onChange={set('career')} /></label>
      <label className="gtac-f gtac-f--wide"><span>Skills <em>comma separated</em></span><input value={f.skills} onChange={set('skills')} /></label>
      <label className="gtac-f gtac-f--wide"><span>Education</span><input value={f.education} onChange={set('education')} placeholder="e.g. Master's degree, computer science" /></label>
      <p className="gtac-edit-note">Salary band and availability come from their record. Your edit is checked again before it can go out.</p>
      <div className="gtac-edit-acts">
        <button type="button" className="gtc-btn-ghost" onClick={onCancel}>Cancel</button>
        <button type="button" className="gtc-primary" disabled={busy} onClick={() => onSave(fromForm(f))}>{busy ? 'Saving…' : 'Save and check'}</button>
      </div>
    </div>
  );
}

export default function GtAnonCards({ authFetch, people, roleId, onStatus, onLeaveOut }) {
  const [cards, setCards] = useState(null);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);
  const [openId, setOpenId] = useState(null);
  const timer = useRef(null);
  const ids = people.map((p) => p.id);
  const key = ids.join(',');
  const role = /^\d+$/.test(String(roleId || '')) ? Number(roleId) : null;

  const prepare = useCallback(async () => {
    if (!key) { setCards([]); return; }
    try {
      const r = await authFetch('/api/anon-cards/prepare', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_ids: key.split(',').map(Number), role_id: role }) });
      setCards(r.cards || []); setErr(null);
    } catch (e) { setErr(e.message); }
  }, [authFetch, key, role]);

  useEffect(() => { setCards(null); prepare(); }, [prepare]);

  // While any card is being written or checked, look again every few seconds.
  const working = (cards || []).some((c) => c.status === 'writing' || c.status === 'checking');
  useEffect(() => {
    if (!working) return undefined;
    timer.current = setTimeout(async () => {
      try {
        const r = await authFetch('/api/anon-cards?candidate_ids=' + key + (role ? '&role_id=' + role : ''));
        setCards(r.cards || []);
      } catch (e) { /* try again next tick */ }
    }, 3000);
    return () => clearTimeout(timer.current);
  }, [working, cards, authFetch, key, role]);

  const byId = {};
  (cards || []).forEach((c) => { byId[c.candidate_id] = c; });
  const clear = ids.filter((id) => byId[id] && byId[id].status === 'clear').length;
  // Ready to send once everyone has a written card. The checks only advise.
  const written = ids.filter((id) => { const c = byId[id]; return c && c.status !== 'writing' && c.card && Object.keys(c.card).length > 0; }).length;
  const flagged = ids.filter((id) => byId[id] && (byId[id].status === 'blocked' || byId[id].status === 'failed')).length;
  useEffect(() => { if (onStatus) onStatus({ clear, written, flagged, total: ids.length, ready: ids.length > 0 && written === ids.length }); }, [clear, written, flagged, ids.length, onStatus]);

  async function act(id, fn) {
    setBusy(id); setErr(null);
    try { await fn(); await prepare(); } catch (e) { setErr(e.message); }
    setBusy(null);
  }
  const rewrite = (c) => act(c.candidate_id, () => authFetch('/api/anon-cards/' + c.id + '/rewrite', { method: 'POST' }));
  const recheck = (c) => act(c.candidate_id, () => authFetch('/api/anon-cards/' + c.id + '/check', { method: 'POST' }));
  const save = (c, card) => act(c.candidate_id, async () => {
    await authFetch('/api/anon-cards/' + c.id, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ card }) });
    setEditing(null);
  });

  if (cards === null && !err) {
    return <div className="gtac-wait"><span className="gtac-spin" />Preparing anonymous cards…</div>;
  }

  return (
    <div className="gtac">
      <div className={'gtac-sum' + (clear === ids.length && ids.length ? ' gtac-sum--ok' : '')}>
        <b>{written < ids.length ? 'Writing cards: ' + written + ' of ' + ids.length + ' done' : flagged ? flagged + ' of ' + ids.length + ' worth a look' : 'All ' + ids.length + ' look clear'}</b>
        <span>Each card is written from the full profile, then checked for details from their real profile and searched on the open web. The checks are advice: you can send any card once it is written.</span>
      </div>
      {err && <div className="gtc-error gtac-err">{err}</div>}
      {people.map((p) => {
        const c = byId[p.id];
        const st = c ? (STATUS[c.status] || STATUS.failed) : STATUS.writing;
        const chk = (c && c.check) || {};
        const open = openId === p.id || (c && (c.status === 'blocked' || c.status === 'failed'));
        const isBusy = busy === p.id || (c && (c.status === 'writing' || c.status === 'checking'));
        const hasCard = c && c.card && Object.keys(c.card).length > 0;
        return (
          <div key={p.id} className={'gtac-item gtac-item--' + (c ? (c.status === 'blocked' ? 'flagged' : c.status) : 'writing')}>
            <div className="gtac-head">
              <button type="button" className="gtac-who gtr2-unbutton" onClick={() => setOpenId(openId === p.id ? null : p.id)}>
                <span className="gtac-name">{p.full_name || 'Unnamed'}</span>
                <span className="gtac-ref">{p.ref || ''}{c && c.edited ? ' · edited by you' : ''}</span>
              </button>
              <span className={'gtac-st ' + st[1]}>{isBusy && <span className="gtac-spin gtac-spin--sm" />}{st[0]}</span>
            </div>

            {c && (c.status === 'blocked' || c.status === 'failed') && (
              <div className="gtac-why gtac-why--warn">
                <b>{c.status === 'blocked' ? 'This card might lead back to them. Your call.' : 'The check could not finish. The card can still be sent.'}</b>
                {chk.reason && <span>{chk.reason}</span>}
                {chk.risky_phrases && chk.risky_phrases.length > 0 && (
                  <span className="gtac-phr">Consider softening: {chk.risky_phrases.map((x, i) => <code key={i}>{x}</code>)}</span>
                )}
                {chk.matched_urls && chk.matched_urls.length > 0 && (
                  <span className="gtac-phr">Found at: {chk.matched_urls.map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer">{hostOf(u)}</a>)}</span>
                )}
              </div>
            )}

            {open && hasCard && editing !== p.id && <AnonCardPreview card={c.card} risky={c.status === 'blocked' ? chk.risky_phrases : null} />}
            {editing === p.id && c && <CardEditor card={c.card} busy={busy === p.id} onCancel={() => setEditing(null)} onSave={(card) => save(c, card)} />}

            {c && editing !== p.id && (
              <div className="gtac-acts">
                {!open && hasCard && <button type="button" className="gtr2-btn gtr2-btn--sm" onClick={() => setOpenId(p.id)}>View card</button>}
                {hasCard && <button type="button" className="gtr2-btn gtr2-btn--sm" disabled={isBusy} onClick={() => { setEditing(p.id); setOpenId(p.id); }}>Edit</button>}
                <button type="button" className="gtr2-btn gtr2-btn--sm" disabled={isBusy} onClick={() => rewrite(c)}>Rewrite</button>
                {c.status === 'failed' && hasCard && <button type="button" className="gtr2-btn gtr2-btn--sm" disabled={isBusy} onClick={() => recheck(c)}>Check again</button>}
                {onLeaveOut && <button type="button" className="gtr2-btn gtr2-btn--sm gtac-leave" onClick={() => onLeaveOut(p.id)}>Leave off this link</button>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
